import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureFlagName, GraphQLFetchPolicies, PersistedStateKeys } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { ClinicPimsIntegrationType, useGetClinicPaymentActivityQuery } from 'shared/types/graphql';
import useFeatureFlag from './useFeatureFlag';
import usePersistedState from './usePersistedState';
import * as Sentry from '@sentry/react';
import { env } from 'env';
const isProduction = env.REACT_APP_PRODUCTION === 'true';

enum HotjarAction {
  Identify = 'identify',
  Event = 'event',
  StateChange = 'stateChange',
}

interface UserAttributes {
  [userAttribute: string]: string | number | Date | boolean;
}

type HotjarIdentify = (
  action: HotjarAction,
  userId: string,
  attributes?: { [userAttribute: string]: string | number | Date | boolean },
) => void;

type HotjarEvent = (event: HotjarAction, action: string) => void;

type HotjarStateChange = (event: HotjarAction, state: string) => void;

declare global {
  interface Window {
    hj: HotjarIdentify | HotjarEvent | HotjarStateChange;
  }
}

const hotjarNotDefined = (): void => {
  console.error('Hotjar is not defined');
};

const useHotJar = (): {
  identify: (userAttributes?: UserAttributes) => void;
  trackEvent: (action: string) => void;
  stateChange: (state: string) => void;
} => {
  const { clinicUser, currentClinicId, hasAdminRoleAtCurrentClinic, currentClinic } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const userId = clinicUser?.id;
  const userEmailAddress = clinicUser?.email;
  const { primaryIntegrationName, allIntegrations } = useIntegrationsProvider();
  const isSortConversationByLastMessageEnabled = isFeatureEnabled(FeatureFlagName.SortConversationByLastMessage);
  const isLoyaltyProgramEnabled = isFeatureEnabled(FeatureFlagName.LoyaltyProgram);
  const clinicHasCareEnabled = isFeatureEnabled(FeatureFlagName.CarePlans);
  const clinicHasDirectBookingEnabled = isFeatureEnabled(FeatureFlagName.DirectBooking);
  const [isReaderModeEnabled] = usePersistedState(PersistedStateKeys.IsReaderModeEnabled, false);
  const response = useGetClinicPaymentActivityQuery({
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onCompleted: (data) => {
      if (!data.clinicPaymentActivity?.success) {
        Sentry.captureException(`[clinicPaymentActivity] request failed: ${data.clinicPaymentActivity?.error}`);
        console.error(data.clinicPaymentActivity?.error);
      }
    },
    onError: (error) => {
      Sentry.captureException(`[clinicPaymentActivity] request failed: ${error}`);
      console.error(error);
    },
  });
  const clinicPaymentActivity = response?.data?.clinicPaymentActivity;

  const formerPPC = useMemo(() => {
    for (const integration of allIntegrations || []) {
      if (integration.type === ClinicPimsIntegrationType.Ppc) return true;
    }
    return false;
  }, [allIntegrations]);

  const clinicHasPaymentsEnabled = useMemo(() => {
    const { submerchantStripeId, submerchantStripeChargesEnabled } = currentClinic || {};
    return !!(submerchantStripeId && submerchantStripeChargesEnabled);
  }, [currentClinic]);

  const clinicPaymentsRecentActivity = useMemo(() => {
    if (!clinicPaymentActivity?.success) {
      return false;
    }

    // More than 2 transactions and more than $50 processed.
    return (
      (clinicPaymentActivity?.totalRecentTransactions || 0) > 2 &&
      (clinicPaymentActivity?.totalRecentCentsProcessed || 0) > 5000
    );
  }, [clinicPaymentActivity]);

  const location = useLocation();

  const shouldHotjarRun = useCallback((): boolean => {
    if (!isProduction || !userId || !userEmailAddress) return false;
    if (!window.hj) {
      console.error('Hotjar is not defined');
      return false;
    }
    return true;
  }, [userEmailAddress, userId]);

  useEffect(() => {
    try {
      if (!shouldHotjarRun()) return;
      window.hj(HotjarAction.StateChange, location.pathname);
    } catch (err) {
      console.log(err);
    }
  }, [location.pathname, shouldHotjarRun]);

  const identify = useCallback(
    (userAttributes: UserAttributes = {}): void => {
      if (window.hj && userId && userEmailAddress && currentClinicId) {
        try {
          if (!shouldHotjarRun()) return;
          const attributes: UserAttributes = {
            email: userEmailAddress,
            clinicId: currentClinicId,
            isAdmin: hasAdminRoleAtCurrentClinic,
            PIMS: primaryIntegrationName || 'None',
            formerPPC,
            isSortConversationByLastMessageEnabled,
            isLoyaltyProgramEnabled,
            isDarkModeEnabled: isReaderModeEnabled,
            clinicHasPaymentsEnabled,
            clinicHasCareEnabled,
            clinicPaymentsRecentActivity,
            clinicHasDirectBookingEnabled,
            ...userAttributes,
          };
          window.hj(HotjarAction.Identify, userId, attributes);
        } catch (err) {
          console.log(err);
        }
      } else {
        hotjarNotDefined();
      }
    },
    [
      userId,
      userEmailAddress,
      currentClinicId,
      shouldHotjarRun,
      hasAdminRoleAtCurrentClinic,
      primaryIntegrationName,
      formerPPC,
      isSortConversationByLastMessageEnabled,
      isLoyaltyProgramEnabled,
      isReaderModeEnabled,
      clinicHasPaymentsEnabled,
      clinicHasCareEnabled,
      clinicPaymentsRecentActivity,
      clinicHasDirectBookingEnabled,
    ],
  );
  const trackEvent = (action: string): void => {
    try {
      if (!shouldHotjarRun()) return;
      window.hj(HotjarAction.Event, action);
    } catch (err) {
      console.log(err);
    }
  };

  const stateChange = (state: string): void => {
    try {
      if (!shouldHotjarRun()) return;
      window.hj(HotjarAction.StateChange, state);
    } catch (err) {
      console.log(err);
    }
  };

  return { identify, trackEvent, stateChange };
};

export default useHotJar;
